import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, IconButton } from "@mui/material";
import * as React from "react";

import { FileDetailsDialog } from "@/shared/components/FileDetails/FileDetailsDialog";
import type { FileRecord } from "@/shared/domain/files";
import { basename } from "@/shared/domain/files/filesystem";
import { isDocumentViewable } from "@/shared/domain/files/fileTypes";
import { usePopupState } from "@/shared/hooks";
import { useDatasources } from "@/shared/state/visualization/hooks";

import { Expandable } from "../../Expandable";
import { FileSelectorSkeleton } from "../../FileSelector";
import { Label } from "../../Label";
import {
  SidebarTab,
  SidebarViewType,
  useWorkspaceSidebar,
} from "../../sidebarContext";
import { EMPTY_KEYWORD_FILTER, type KeywordFilter } from "../../Topics/filter";
import {
  useEphemeralWorkspaceState,
  useEphemeralWorkspaceStateLoading,
  useWorkspaceFiles,
} from "../../WorkspaceCtx/hooks";
import { FilterInput } from "../FilterInput";
import { makeMatcher } from "../FilterInput/makeMatcher";
import { FilterMatch } from "../filterResult";

import styles from "./FileList.module.css";

interface FileFilterResult {
  file: FileRecord;
  match?: FilterMatch;
}

/**
 * Lists all workspace files grouped by type (topic-laden, document-viewable, other).
 * Actionable files (topic-laden or document-viewable) navigate to the Topics tab on click.
 * A hover-revealed info button opens a File Details dialog.
 */
export function FileList() {
  const files = useWorkspaceFiles();
  const { topics } = useEphemeralWorkspaceState();
  const isLoading = useEphemeralWorkspaceStateLoading();
  const sidebar = useWorkspaceSidebar();
  const datasources = useDatasources();

  // Estimate the number of skeleton rows to render while files are loading.
  // Prefer the resolved file count; otherwise use the count of file datasources;
  // otherwise assume a dataset with many files.
  const estimatedFileCount = React.useMemo(() => {
    if (files.length > 0) {
      return files.length;
    }
    const fileDatasources = datasources.filter((ds) => ds.type === "file");
    return fileDatasources.length > 0 ? fileDatasources.length : 10;
  }, [files.length, datasources]);

  const [filter, setFilter] =
    React.useState<KeywordFilter>(EMPTY_KEYWORD_FILTER);

  const detailsPopup = usePopupState();
  const { open: openDetailsPopup } = detailsPopup;
  const [detailsFile, setDetailsFile] = React.useState<FileRecord | undefined>(
    undefined,
  );

  const openDetails = React.useCallback(
    (file: FileRecord) => {
      setDetailsFile(file);
      openDetailsPopup();
    },
    [openDetailsPopup],
  );

  const keyRef = React.useRef(0);

  const navigateToTopics = React.useCallback(
    (fileId: string) => {
      keyRef.current += 1;

      sidebar.openView({
        type: SidebarViewType.TabbedSidebar,
        data: {
          initialSelectedFileId: {
            fileId,
            key: keyRef.current,
          },
        },
      });
      sidebar.setActiveTab(SidebarTab.Topics);
    },
    [sidebar],
  );

  const fileIdsWithTopics = React.useMemo(() => {
    return new Set(topics.map((t) => t.association.association_id));
  }, [topics]);

  const matcher = React.useMemo(() => makeMatcher(filter), [filter]);

  const { topicFiles, documentFiles, otherFiles } = React.useMemo(() => {
    const topic: FileFilterResult[] = [];
    const document: FileFilterResult[] = [];
    const other: FileFilterResult[] = [];

    for (const file of files) {
      const name = file.relative_path
        ? basename(file.relative_path)
        : file.file_id;

      const result = matcher(name);
      if (!result.matches) {
        continue;
      }

      const match: FilterMatch | undefined = result.position
        ? { position: result.position }
        : undefined;
      const fileResult: FileFilterResult = { file, match };

      if (fileIdsWithTopics.has(file.file_id)) {
        topic.push(fileResult);
      } else if (isDocumentViewable(name)) {
        document.push(fileResult);
      } else {
        other.push(fileResult);
      }
    }

    return { topicFiles: topic, documentFiles: document, otherFiles: other };
  }, [files, fileIdsWithTopics, matcher]);

  if (isLoading) {
    return (
      <FileSelectorSkeleton
        className={styles.skeletonContainer}
        estimatedFileCount={estimatedFileCount}
      />
    );
  }

  if (files.length === 0) {
    return (
      <div className={styles.container}>
        <Alert severity="info" className={styles.emptyAlert}>
          No files in this workspace.
        </Alert>
      </div>
    );
  }

  const groups = [topicFiles, documentFiles, otherFiles].filter(
    (g) => g.length > 0,
  );

  return (
    <div className={styles.container}>
      <FilterInput
        className={styles.filter}
        placeholder="Filter files by name"
        onFilterChange={setFilter}
      />
      <div className={styles.content}>
        {groups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && <hr className={styles.separator} />}
            {group.map((result) => {
              const { file, match } = result;
              const name = file.relative_path
                ? basename(file.relative_path)
                : file.file_id;
              const hasTopics = fileIdsWithTopics.has(file.file_id);
              const actionable = hasTopics || isDocumentViewable(name);

              return (
                <div key={file.file_id} className={styles.fileRow}>
                  <Expandable
                    data={file.file_id}
                    className={styles.fileName}
                    onClick={actionable ? navigateToTopics : undefined}
                    disabled={!actionable}
                  >
                    <Label
                      text={name}
                      title={file.relative_path}
                      match={match}
                    />
                  </Expandable>
                  <IconButton
                    className={styles.detailsButton}
                    size="small"
                    title="View file details"
                    aria-label="View file details"
                    onClick={() => openDetails(file)}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <FileDetailsDialog
        open={detailsPopup.isOpen}
        onClose={detailsPopup.close}
        fileRecord={detailsFile}
      />
    </div>
  );
}
